import React, { useState, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import shortid from 'shortid'

import { GraphQLState } from '../store/graphql-reducer'
import { OauthState } from '../store/oauth-reducer'
import Row, { Slot } from './row'
import styles from '../component-styles/connected-websites.module.scss'
import {
  getSelectedAccountAsync,
  getUserDetailsAsync,
  switchAccountAsync,
} from '../store/actions'
import {
  removeAccount,
  // getAllAccountsConnectionStatus,
  connectToGoogle,
  connectToAdobe,
  reconnectGA,
  // reconnectAA,
} from '../api-client/settings-client'
import { getAccountDataSource } from '../api-client/graphql-client'
// import useAPI from '../hooks/useAPI'
import Modal, { ModalBody, ModalHead, ModalFooter } from './modal'
import Heading from './heading'
import WebsitePicker from './website-picker'
import DeleteButtonWithConfirmation from './delete-button-with-confirmation'
import { accountTypes } from '../core/variables'
import ConfirmationButtons from './confirmation-buttons'
import Button from './button'
// import { useHistory } from 'react-router'
import { getItemByKeyValue, sortData } from '../helpers'
import ReconnectAAModal from './reconnect-aa-modal'
import { Preloader } from './loader'

interface Source {
  accountID: string
  dataSource: {
    kind: DataSource
    connected: boolean
  }
}

export default function ConnectedWebsites() {
  const [dataSources, setDataSources] = useState<
    {
      accountID: string
      dataSource: {
        connected: boolean
        kind: DataSource
      }
    }[]
  >([])
  const [addNew, setAddNew] = useState(false)
  const [reconnectJourney, setReconnectJourney] = useState(false)
  const [reconnectAccountId, setReconnectAccountId] = useState('')
  const [account, setAccount] = useState('')
  const [showAAReconnectModal, setShowAAReconnectModal] = useState(false)
  const [labelList, setLabelList] = useState<OrganisationAccountInfo[]>([])

  const dispatch = useDispatch()
  const {
    oauth,
    graphql,
  }: { oauth: OauthState; graphql: GraphQLState } = useSelector(
    (state: any): any => state,
  )
  const {
    credentials,
    dataSource,
    state: { openModal, reconnect, reconnectId },
  } = oauth

  const { currentUser, currentAccount } = graphql

  // const { userAccountProfiles } = currentUser || {}

  const isPowerUser = currentUser?.currentAccountPermission === 'support'
  // const [status, refetch] = useAPI(getAllAccountsConnectionStatus)

  const useAccountDataSource = useMemo(() => {
    if (dataSources && reconnectAccountId) {
      const found = getItemByKeyValue(
        dataSources,
        'accountID',
        reconnectAccountId,
      )
      if (found) {
        const {
          dataSource: { kind, connected },
        } = found
        return {
          connected,
          dataSource: kind,
        }
      }
    }
    return { dataSource: 'GA_VIEW', connected: false }
  }, [dataSources, reconnectAccountId])

  const onClick = () => {
    if (reconnectJourney) {
      if (useAccountDataSource.dataSource === 'GA_VIEW') {
        connectToGoogle({
          returnTo: '/settings',
          oauthJourney: true,
          reconnectId: reconnectAccountId,
          openModal: false,
          reconnect: true,
        })
      } else if (useAccountDataSource.dataSource === 'AA_REPORT') {
        connectToAdobe({
          returnTo: '/settings',
          oauthJourney: true,
          reconnectId: reconnectAccountId,
          openModal: false,
          reconnect: true,
        })
      }
    } else {
      connectToGoogle({
        returnTo: '/settings',
        oauthJourney: true,
        openModal: true,
      })
    }
  }

  const refreshAccount = (force = false) => {
    if (account !== '') {
      if (currentAccount && currentAccount.accountID) {
        if (force || currentAccount.accountID !== account) {
          dispatch(switchAccountAsync(currentAccount.accountID))
        }
      }
    }
  }

  const actionReconnect = async (accountID) => {
    let res = false

    if (dataSource === 'GA_VIEW') {
      res = await reconnectGA({
        credentials,
        accountID,
      })
      if (!res) {
        console.error(`Cannot reconnect account ID: ${accountID}`)
        return
      }

      refreshAccount(true)
    } else if (dataSource === 'AA_REPORT') {
      // AA needs one more step before re-connecting
      setShowAAReconnectModal(true)
    }
  }

  const accountList = useMemo(() => {
    const defaultAccountList = [...(currentUser?.userAccountProfiles || [])]
    // if (status) {
    //   const dict: { [key: string]: any } = {}

    //   status.accountConnectionStatus.forEach((acc) => {
    //     dict[acc.accountID] = acc
    //   })

    //   accountList = accountList.map((acc) => ({
    //     ...dict[acc.accountID],
    //     ...acc,
    //   }))
    // }

    const createLabelList = (_accountList, sources: any = null) => {
      const acc = {}

      _accountList.forEach((item) => {
        const useAcc: AccountInfo[] = acc[item.companyID]
          ? acc[item.companyID].accts
          : []

        const enhancedDataItem = { ...item }
        if (sources && Array.isArray(sources)) {
          const { accountID } = item
          const found = getItemByKeyValue(sources, 'accountID', accountID)
          if (found) {
            const {
              dataSource: { kind, connected },
            } = found
            enhancedDataItem.dataSource = kind
            enhancedDataItem.connected = connected
          }
        }

        acc[item.companyID] = {
          title: item.companyName,
          accts: [...useAcc, enhancedDataItem],
        }
      })

      const list: OrganisationAccountInfo[] = Object.keys(acc).map((key) => {
        return { title: acc[key].title, accounts: acc[key].accts }
      })
      return sortData(list, 'title', true, null, true)
    }

    const getFullList = async (_accountList) => {
      let sources: Source[] = []

      const { data: accountDataSources } = await getAccountDataSource(
        _accountList.map((acct) => acct.accountID),
      )
      sources = Object.values(accountDataSources)
      setDataSources(sources)
      const res = createLabelList(_accountList, sources)
      setLabelList(res)
    }

    getFullList(defaultAccountList)

    const res = createLabelList(defaultAccountList)
    setLabelList(res)

    return defaultAccountList
  }, [currentUser])

  useEffect(() => {
    if (openModal && credentials !== '') {
      setAddNew(true)
    }
    // eslint-disable-next-line
  }, [openModal])

  useEffect(() => {
    if (reconnect && reconnectId !== '') {
      actionReconnect(reconnectId)
    }
    // eslint-disable-next-line
  }, [reconnect, reconnectId])

  useEffect(() => {
    if (currentAccount && currentAccount.accountID !== '') {
      setAccount(currentAccount && currentAccount.accountID)
    }
  }, [currentAccount])

  useEffect(() => {
    refreshAccount()
    // eslint-disable-next-line
  }, [account, currentAccount])

  const onToggle = () => {
    setAddNew(!addNew)
  }

  const reconnectWebsite = async (accountID: string) => {
    setReconnectAccountId(accountID)
    if (credentials !== '') {
      actionReconnect(accountID)
    } else {
      setReconnectJourney(true)
      setAddNew(true)
    }
  }

  if (
    accountList.length < 1 ||
    (accountList.length === 1 && currentAccount && currentAccount.blankAccount)
  ) {
    return (
      <>
        <p className={styles.byline}>
          Please complete your setup journey by connecting your first website.
        </p>
        <Button
          className={styles.centerButton}
          href="/connect"
          style={{ maxWidth: '200px' }}
        >
          Connect your website
        </Button>
      </>
    )
  }

  // dataSource !== 'AA_REPORT' &&

  const getIconPath = (homepage: string | null): string => {
    return homepage
      ? `https://www.google.com/s2/favicons?domain=${homepage}`
      : `/favicon.png`
  }

  return (
    <>
      <p className={styles.byline}>
        Reconnect any accounts which have been disconnected due to credential
        changes.
      </p>
      <div className={styles.wrapper}>
        <Row className={styles.header}>
          <Slot align="flex-start" vAlign="center" width={586}>
            Website
          </Slot>
          <Slot align="flex-start" vAlign="center" width={160}>
            Status
          </Slot>
          {isPowerUser ? (
            <Slot align="flex-start" vAlign="center" width={64}>
              Account
            </Slot>
          ) : null}
          <Slot width={64} />
        </Row>

        <div className={styles.body}>
          {labelList.length === 0 && (
            <Preloader style={{ width: 30, height: 20, margin: '32px auto' }} />
          )}
          {labelList.map(({ title, accounts }) => {
            return (
              <div key={shortid.generate()}>
                {labelList.length > 1 && (
                  <span className={styles.orgLabel}>
                    <span>{title}</span>
                  </span>
                )}
                {accounts.map(
                  (
                    {
                      homepage,
                      accountName,
                      accountID,
                      dataSource: currentDataSource = '',
                      connected,
                    }: AccountInfo,
                    index,
                  ) => {
                    return (
                      <Row
                        key={`${accountName}${homepage}${accountID}`}
                        className={styles.bodyItem}
                        vAlign="center"
                        align="flex-start"
                      >
                        <Slot
                          align="flex-start"
                          vAlign="center"
                          className={styles.bodyItemSlot}
                          padding="0 20px 0 0"
                          width={40}
                        >
                          <img
                            src={getIconPath(homepage)}
                            alt="favicon"
                            className={styles.icon}
                          />
                        </Slot>
                        <Slot
                          align="flex-start"
                          vAlign="center"
                          className={styles.bodyItemSlot}
                          width={546}
                        >
                          <h3>{accountName}</h3>
                          <p className={styles.bodyItemP}>{homepage}</p>
                        </Slot>

                        <Slot
                          align="flex-start"
                          vAlign={
                            typeof connected !== 'undefined'
                              ? 'center'
                              : 'flex-start'
                          }
                          width={160}
                        >
                          {typeof connected !== 'undefined' ? (
                            <button
                              className={
                                connected
                                  ? styles.connectedActive
                                  : styles.connectedInactive
                              }
                              onClick={() => {
                                // HACK: Tom's requirement - make always clickable for Adobe
                                if (
                                  currentDataSource === 'AA_REPORT' ||
                                  !connected
                                ) {
                                  reconnectWebsite(accountID)
                                }
                              }}
                            >
                              {connected ? 'Connected' : 'Reconnect'}
                            </button>
                          ) : (
                            <Preloader
                              style={{ width: 30, height: 20, margin: 0 }}
                            />
                          )}
                        </Slot>
                        {isPowerUser ? (
                          <Slot align="flex-start" vAlign="center" width={64}>
                            <p className={styles.bodyItemP}>
                              {accountTypes[currentDataSource]
                                ? accountTypes[currentDataSource].short
                                : 'unknown'}
                            </p>
                          </Slot>
                        ) : null}
                        <Slot align="flex-end" vAlign="center" width={64}>
                          <DeleteButtonWithConfirmation
                            odd={index % 2 === 1}
                            onClick={async () => {
                              await removeAccount({ accountID })
                              dispatch(getSelectedAccountAsync())
                              dispatch(getUserDetailsAsync())
                            }}
                          >
                            <p>
                              Are you sure you want to disconnect{' '}
                              <strong>{homepage}</strong>?
                            </p>
                          </DeleteButtonWithConfirmation>
                        </Slot>
                      </Row>
                    )
                  },
                )}
              </div>
            )
          })}
        </div>
        <Row className={styles.footer} align="flex-end" vAlign="center">
          <Slot align="flex-end" vAlign="center">
            <Button
              className={styles.upgradeButton}
              onClick={() => setAddNew((s) => !s)}
            >
              Add Website
            </Button>
          </Slot>
        </Row>
      </div>
      {addNew && (
        <Modal narrow type="white" active={addNew} toggleActive={onToggle}>
          <ModalHead>
            <Heading type={3} align="left">
              Connect your website
            </Heading>
          </ModalHead>
          {credentials === '' ? (
            <>
              <ModalBody>
                <div className={styles.center}>
                  <p>
                    Please re-connect your account with{' '}
                    {accountTypes[useAccountDataSource.dataSource].long}.
                  </p>
                  <Button onClick={onClick}>
                    Connect with{' '}
                    {accountTypes[useAccountDataSource.dataSource].long}
                  </Button>
                </div>
              </ModalBody>
              <ModalFooter>
                <ConfirmationButtons
                  disabled={credentials === ''}
                  hideDisabled={credentials === ''}
                  type="simple"
                  onYes={(): void => {
                    onToggle()
                  }}
                  onNo={(): void => {
                    onToggle()
                  }}
                  yesLabel="Connect website"
                  noLabel="Go back"
                />
              </ModalFooter>
            </>
          ) : (
            <WebsitePicker
              modal
              onToggle={onToggle}
              onClick={() => {
                setAddNew((s) => !s)
                dispatch(getSelectedAccountAsync())
                dispatch(getUserDetailsAsync())
                // refetch()
                window.location.reload()
              }}
            />
          )}
        </Modal>
      )}
      {showAAReconnectModal && (
        <ReconnectAAModal
          selectedAccountId={reconnectId || reconnectAccountId}
          onToggle={() => {
            setShowAAReconnectModal(false)
          }}
          currentAccount={currentAccount?.accountID || ''}
        />
      )}
    </>
  )
}
